import {
  ContextualSaveBar,
  Card,
  Page,
  BlockStack,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { EntityType } from "@shared/types/commonTypes";
import { useParams } from "react-router-dom";
import { useState } from "react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { ComplianceSchema, ComplianceData } from "./types";
import { SettingsPageWrapper } from "@shared/ui/Settings";
import ComplianceForm from "./Forms/ComplianceForm";
import CoreForm from "./Forms/CoreForm";

const CompliancePage = () => {
  const { t } = useTranslation();
  const { GET, PUT } = useApiRequest();
  const { clinicId } = useParams<{ clinicId: string }>();
  const resolvedClinicId = clinicId ?? "";
  const [dataDraft, setDataDraft] = useState<ComplianceData | null>(null);
  const [originalDataDraft, setOriginalDataDraft] =
    useState<ComplianceData | null>(null);
  const [showSaveBar, setShowSaveBar] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Fetch schema
  const {
    isLoading: isSchemaLoading,
    data: complianceSchema,
  } = useQuery<ComplianceSchema[]>({
    queryKey: ["compliance-schema", clinicId],
    queryFn: async () =>
      GET<ComplianceSchema[]>(
        `/api/healthcare-companies/${clinicId}/compliance/schema`
      ),
  });

  // Fetch compliance data
  const {
    isLoading: isDataLoading,
    data: complianceDataResponse,
  } = useQuery<ComplianceData>({
    queryKey: ["compliance-data", clinicId],
    queryFn: async () =>
      GET<ComplianceData>(`/api/healthcare-companies/${clinicId}/compliance`),
  });

  const schemas = complianceSchema ?? [];
  const data = dataDraft ?? complianceDataResponse ?? {};
  const originalData = originalDataDraft ?? complianceDataResponse ?? {};

  // Validate required fields
  const validateData = () => {
    const errors: Record<string, string> = {};
    schemas.forEach((schema) => {
      schema.groups.forEach((group) => {
        group.properties.forEach((property) => {
          if (property.isRequired) {
            if (property.renderer.id === "checkbox") {
              if (data[property.name] === undefined) {
                errors[property.name] = t("common.requiredField", {
                  field_name: property.label,
                });
              }
            } else if (!data[property.name]) {
              errors[property.name] = t("common.requiredField", {
                field_name: property.label,
              });
            }
          }
        });
      });
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  // Save compliance data
  const saveComplianceMutation = useMutation({
    mutationFn: async () => {
      if (!validateData()) {
        console.error("Validation errors:", validationErrors);
        return;
      }

      await PUT(`/api/healthcare-companies/${clinicId}/compliance`, data);
    },
    onSuccess: () => {
      setShowSaveBar(false);
      setOriginalDataDraft(data);
    },
    onError: (error) => {
      console.error("Error saving compliance data:", error);
    },
  });

  const handleSave = () => {
    saveComplianceMutation.mutate();
  };

  const handleDiscard = () => {
    setDataDraft(originalData);
    setValidationErrors({});
    setShowSaveBar(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDataChange = (field: string, value: any) => {
    setDataDraft((prev) => {
      const baseData = prev ?? data;
      return {
        ...baseData,
        [field]: value,
      };
    });
    setShowSaveBar(true);

    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors[field];
        return updatedErrors;
      });
    }
  };

  const coreGroups = schemas
    .flatMap((schema) => schema.groups)
    .filter((group) => group.name === "core");

  const otherGroups = schemas
    .flatMap((schema) => schema.groups)
    .filter((group) => group.name !== "core");

  return (
    <SettingsPageWrapper entityType={EntityType.CLINIC} entityId={resolvedClinicId}>
      <Page title={t("compliance.title")} narrowWidth>
        {showSaveBar && (
          <ContextualSaveBar
            backgroundColor="#2B0069"
            saveAction={{
              onAction: handleSave,
              loading: saveComplianceMutation.isPending,
            }}
            discardAction={{
              onAction: handleDiscard,
            }}
          />
        )}

        <BlockStack gap="400">
          {isSchemaLoading || isDataLoading ? (
            <>
              <Card>
                <SkeletonBodyText lines={3} />
              </Card>
              <Card>
                <SkeletonBodyText lines={6} />
              </Card>
            </>
          ) : (
            <>
              {coreGroups.length > 0 && (
                <Card>
                  <BlockStack gap="300">
                    {coreGroups.map((coreGroup) => (
                      <CoreForm
                        key={coreGroup.name}
                        schema={coreGroup}
                        data={data}
                        onDataChange={handleDataChange}
                      />
                    ))}
                  </BlockStack>
                </Card>
              )}

              {otherGroups.length > 0 && (
                <Card>
                  <BlockStack gap="300">
                    {otherGroups.map((otherGroup) => (
                      <ComplianceForm
                        key={otherGroup.name}
                        schema={otherGroup}
                        data={data}
                        onDataChange={handleDataChange}
                        validationErrors={validationErrors}
                      />
                    ))}
                  </BlockStack>
                </Card>
              )}
            </>
          )}
        </BlockStack>
      </Page>
      <br />
    </SettingsPageWrapper>
  );
};

export default CompliancePage;
